import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Loader2 } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { email: "", area: "", max_budget: "", currency: "NGN", min_beds: "any", market: "all" };
const input = "mt-2 h-[46px] w-full bg-transparent border border-white/50 px-3 text-base normal-case tracking-normal text-white outline-none focus:border-white placeholder:text-white/40";
const trig = "mt-2 h-[46px] rounded-none bg-transparent border-white/50 text-white normal-case tracking-normal text-base";

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={trig} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="rounded-none">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const ListingAlerts = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    const s = params.get("alerts");
    if (!s) return;
    const msg = { confirmed: "Alerts confirmed — we'll email you when a matching property is listed.",
      unsubscribed: "You've been unsubscribed from property alerts.", invalid: "That alert link has expired." }[s];
    if (msg) toast(msg);
    params.delete("alerts");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/alerts", {
        email: form.email, area: form.area.trim(), currency: form.currency, market: form.market,
        max_budget: form.max_budget ? Number(form.max_budget) : null,
        min_beds: form.min_beds === "any" ? null : Number(form.min_beds),
      });
      toast.success("Nearly done — check your inbox and click the link to confirm your alerts.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-gw-deep text-white py-20 md:py-28" data-testid="listing-alerts-section">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1fr_1.6fr] gap-12">
        <div>
          <p className="lab text-gw-mist inline-flex items-center gap-2"><BellRing size={13} /> Listing alerts</p>
          <h2 className="lux gw-h2 mt-4">Be first to new listings.</h2>
          <p className="gw-body text-gw-pale mt-4 max-w-md">Tell us your area and budget. We'll email you when a matching property is listed — nothing else.</p>
        </div>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="listing-alerts-form">
          <label className="lab sm:col-span-2">Email
            <input required type="email" value={form.email} onChange={set("email")} className={input} data-testid="alerts-email-input" />
          </label>
          <label className="lab">Area (optional)
            <input value={form.area} onChange={set("area")} placeholder="e.g. Lekki, Dubai" className={input} data-testid="alerts-area-input" />
          </label>
          <div className="lab">Market
            <Pick value={form.market} onChange={set("market")} testId="alerts-market-select"
              options={[["all", "Local & international"], ["local", "Local only"], ["international", "International only"]]} />
          </div>
          <label className="lab">Max budget (optional)
            <input inputMode="numeric" value={form.max_budget} onChange={(e) => set("max_budget")(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="No limit" className={input} data-testid="alerts-budget-input" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div className="lab">Currency
              <Pick value={form.currency} onChange={set("currency")} testId="alerts-currency-select" options={[["NGN", "₦ NGN"], ["USD", "$ USD"], ["GBP", "£ GBP"], ["EUR", "€ EUR"], ["CAD", "C$ CAD"]]} />
            </div>
            <div className="lab">Bedrooms
              <Pick value={form.min_beds} onChange={set("min_beds")} testId="alerts-beds-select"
                options={[["any", "Any"], ...[1, 2, 3, 4, 5].map((n) => [String(n), `${n}+`])]} />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="alerts-submit-button"
            className="sm:col-span-2 lab bg-white text-gw-onwhite px-6 py-4 inline-flex justify-center items-center gap-2 transition-colors duration-200 hover:bg-gw-mist disabled:opacity-60">
            {busy && <Loader2 size={14} className="animate-spin" />} Alert me
          </button>
        </form>
      </div>
    </section>
  );
};
